import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Upload, Download, CheckCircle, XCircle, AlertTriangle, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

const OASISSeguePage = () => {
  const [assessments, setAssessments] = useState([
    { id: '1', patient: 'Smith, John', mrn: 'MRN-001', type: 'SOC', assessmentDate: '01/25/2026', status: 'Ready', errors: 0, warnings: 2 },
    { id: '2', patient: 'Johnson, Mary', mrn: 'MRN-002', type: 'ROC', assessmentDate: '01/24/2026', status: 'Ready', errors: 0, warnings: 0 },
    { id: '3', patient: 'Williams, Robert', mrn: 'MRN-003', type: 'Recert', assessmentDate: '01/23/2026', status: 'Errors', errors: 3, warnings: 1 },
    { id: '4', patient: 'Brown, Patricia', mrn: 'MRN-004', type: 'Discharge', assessmentDate: '01/22/2026', status: 'Submitted', errors: 0, warnings: 0 },
    { id: '5', patient: 'Davis, Michael', mrn: 'MRN-005', type: 'SOC', assessmentDate: '01/21/2026', status: 'Accepted', errors: 0, warnings: 0 },
    { id: '6', patient: 'Garcia, Jennifer', mrn: 'MRN-006', type: 'Transfer', assessmentDate: '01/20/2026', status: 'Ready', errors: 0, warnings: 4 },
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedAssessments, setSelectedAssessments] = useState([]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedAssessments(assessments.map(a => a.id));
    } else {
      setSelectedAssessments([]);
    }
  };

  const handleSelect = (id) => {
    setSelectedAssessments(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSubmit = () => {
    if (selectedAssessments.length === 0) {
      toast.error('Please select assessments to submit');
      return;
    }
    const readyAssessments = assessments.filter(a => selectedAssessments.includes(a.id) && a.status === 'Ready');
    if (readyAssessments.length === 0) {
      toast.error('No ready assessments selected');
      return;
    }
    toast.success(`Submitting ${readyAssessments.length} assessment(s) to CMS...`);
    setAssessments(prev => prev.map(a => 
      selectedAssessments.includes(a.id) && a.status === 'Ready' ? { ...a, status: 'Submitted' } : a
    ));
    setSelectedAssessments([]);
  };

  const handleValidate = () => {
    if (selectedAssessments.length === 0) {
      toast.error('Please select assessments to validate');
      return;
    }
    toast.success(`Validating ${selectedAssessments.length} assessment(s)...`);
  };

  const getStatusBadge = (status, errors, warnings) => {
    switch (status) {
      case 'Ready':
        return warnings > 0 
          ? <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Ready ({warnings} warnings)</span>
          : <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Ready</span>;
      case 'Errors':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> {errors} Error(s)</span>;
      case 'Submitted':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Submitted</span>;
      case 'Accepted':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Accepted</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const filteredAssessments = assessments.filter(a => {
    const matchesSearch = a.patient.toLowerCase().includes(searchQuery.toLowerCase()) || a.mrn.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || a.status.toLowerCase() === filterStatus.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="oasis-segue-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#9b59b6] rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">OASIS Segue</h1>
              <p className="text-gray-600">Validate and submit OASIS assessments to CMS</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleValidate}>
              <CheckCircle className="w-4 h-4 mr-2" /> Validate Selected
            </Button>
            <Button className="bg-[#9b59b6] hover:bg-[#8e44ad]" onClick={handleSubmit}>
              <Upload className="w-4 h-4 mr-2" /> Submit to CMS
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-gray-900">{assessments.length}</p>
            <p className="text-sm text-gray-500">Total</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-green-600">{assessments.filter(a => a.status === 'Ready').length}</p>
            <p className="text-sm text-gray-500">Ready</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-red-600">{assessments.filter(a => a.status === 'Errors').length}</p>
            <p className="text-sm text-gray-500">With Errors</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-blue-600">{assessments.filter(a => a.status === 'Submitted').length}</p>
            <p className="text-sm text-gray-500">Submitted</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-green-600">{assessments.filter(a => a.status === 'Accepted').length}</p>
            <p className="text-sm text-gray-500">Accepted</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by patient name or MRN..."
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Status:</Label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All</option>
                <option value="ready">Ready</option>
                <option value="errors">Errors</option>
                <option value="submitted">Submitted</option>
                <option value="accepted">Accepted</option>
              </select>
            </div>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
          </div>
        </div>

        {/* Assessments Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input type="checkbox" onChange={handleSelectAll} checked={selectedAssessments.length === assessments.length} className="rounded" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">MRN</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Assessment Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAssessments.map((assessment) => (
                  <tr key={assessment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input 
                        type="checkbox" 
                        checked={selectedAssessments.includes(assessment.id)} 
                        onChange={() => handleSelect(assessment.id)} 
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{assessment.patient}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono">{assessment.mrn}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">{assessment.type}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{assessment.assessmentDate}</td>
                    <td className="px-4 py-3">{getStatusBadge(assessment.status, assessment.errors, assessment.warnings)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => toast.info(`Viewing ${assessment.patient}`)}>View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OASISSeguePage;
